import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useOpenApi } from '@/hooks/useOpenApi';
import { useRecent } from '@/hooks/useRecent';
import { findEndpoint, resolveSchema } from '@/lib/openapi/parse';
import MethodBadge from '@/components/layout/MethodBadge';
import ParamTable from '@/components/endpoint/ParamTable';
import SchemaViewer from '@/components/endpoint/SchemaViewer';
import CodeSamples from '@/components/code/CodeSamples';
import CodeBlock from '@/components/code/CodeBlock';
import AskAiBar from '@/components/ai/AskAiBar';
import AiShortcutMenu from '@/components/ai/AiShortcutMenu';
import ConnectCta from '@/components/ConnectCta';
import TryItForm from '@/components/try/TryItForm';
import EndpointResources from '@/components/permalink/EndpointResources';
import { Loader2, Copy } from 'lucide-react';
import { showToast } from '@/lib/toast';
import { useApiKey } from '@/stores/apiKey.store';
import type { Method } from '@/lib/tokens';

export default function EndpointPage() {
    const { operationId = '' } = useParams();
    const { spec, loading, error } = useOpenApi();
    const { key } = useApiKey();

    const found = useMemo(() => spec ? findEndpoint(spec, operationId) : null, [spec, operationId]);

    // Track visit in recent endpoints sidebar history
    useRecent(found ? {
        operationId: found.summary.operationId,
        method: found.summary.method.toUpperCase(),
        path: found.summary.path,
        summary: found.summary.summary,
    } : null);

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-ghost" />
            </div>
        );
    }
    if (error) {
        return <ErrorState message={error} />;
    }
    if (!found || !spec) {
        return <ErrorState message="Endpoint tidak ditemukan dalam spec." />;
    }

    const { summary, op } = found;
    const methodUpper = summary.method.toUpperCase() as Method;
    const headerParams = (op.parameters || []).filter((p: any) => p.in === 'header');
    const pathParams = (op.parameters || []).filter((p: any) => p.in === 'path');
    const queryParams = (op.parameters || []).filter((p: any) => p.in === 'query');
    const bodySchema = op.requestBody?.content?.['application/json']?.schema;
    const responses = Object.entries(op.responses || {}) as [string, any][];

    const sampleBody = bodySchema ? buildSampleFromSchema(spec, resolveSchema(spec, bodySchema)) : undefined;

    const handleCopyLink = () => {
        try { navigator.clipboard.writeText(window.location.href); showToast('Link halaman tersalin'); }
        catch { /* */ }
    };

    return (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_440px]">
            <div className="px-6 lg:px-8 py-6 max-w-2xl">
                {!key && (
                    <ConnectCta
                        title="Login dengan SatuTim untuk coba endpoint ini"
                        description="Aktifkan akses API di pengaturan workspace, lalu kembali ke sini. Fitur Try-it & Ask AI akan auto-aktif setelah key tersinkron."
                    />
                )}

                {/* Tag breadcrumb */}
                <div className="text-xs mb-2" style={{ color: 'var(--ghost)' }}>
                    {summary.tag}
                </div>

                {/* Title */}
                <h1 className="text-2xl font-semibold tracking-tight mb-3" style={{ color: 'var(--ink)' }}>
                    {summary.summary}
                </h1>

                {summary.description && (
                    <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--muted)' }}>
                        {summary.description}
                    </p>
                )}

                {/* URL bar + actions */}
                <div className="mb-8 flex items-center gap-2 flex-wrap">
                    <div className="inline-flex items-center gap-2 px-3 h-9 rounded-md font-mono text-xs"
                        style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}>
                        <MethodBadge method={methodUpper} size="sm" />
                        <code style={{ color: 'var(--ink)' }}>{summary.path}</code>
                    </div>
                    <button
                        onClick={handleCopyLink}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs transition-colors"
                        style={{
                            backgroundColor: 'var(--bg)',
                            color: 'var(--muted)',
                            border: '1px solid var(--border)',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = 'var(--ink)';
                            e.currentTarget.style.borderColor = 'var(--border-strong)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = 'var(--muted)';
                            e.currentTarget.style.borderColor = 'var(--border)';
                        }}
                    >
                        <Copy className="h-3 w-3" /> Salin halaman
                    </button>
                </div>

                {/* Sections */}
                {headerParams.length > 0 && <ParamTable title="Header" params={headerParams} />}
                {pathParams.length > 0 && <ParamTable title="Parameter Path" params={pathParams} />}
                {queryParams.length > 0 && <ParamTable title="Parameter Query" params={queryParams} />}

                {bodySchema && (
                    <div className="mb-6">
                        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>Body</h3>
                        <div className="p-4 rounded-md"
                            style={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                            <SchemaViewer schema={bodySchema} spec={spec} />
                        </div>
                    </div>
                )}

                {/* Try It */}
                <section className="mt-8 mb-8 pt-6" style={{ borderTop: '1px solid var(--border)' }}>
                    <h2 className="text-base font-semibold mb-3" style={{ color: 'var(--ink)' }}>Try It</h2>
                    <TryItForm
                        operationId={summary.operationId}
                        method={methodUpper}
                        path={summary.path}
                        op={op}
                        spec={spec}
                    />
                </section>

                {responses.length > 0 && (
                    <div className="mb-8">
                        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>Response</h3>
                        <div className="space-y-3">
                            {responses.map(([code, resp]) => {
                                const r = resp.$ref ? resolveSchema(spec, resp) : resp;
                                const isSuccess = code.startsWith('2');
                                return (
                                    <div key={code} className="p-4 rounded-md"
                                        style={{ backgroundColor: 'var(--bg-input)', border: '1px solid var(--border)' }}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <code className="text-xs font-semibold px-1.5 py-0.5 rounded font-mono"
                                                style={{
                                                    backgroundColor: isSuccess ? 'var(--accent-bg)' : 'var(--err-bg)',
                                                    color: isSuccess ? 'var(--accent)' : 'var(--err)',
                                                    border: `1px solid ${isSuccess ? 'var(--accent-border)' : 'var(--err-border)'}`,
                                                }}>
                                                {code}
                                            </code>
                                            <span className="text-xs" style={{ color: 'var(--muted)' }}>{r.description}</span>
                                        </div>
                                        {r.content?.['application/json']?.schema && (
                                            <SchemaViewer schema={r.content['application/json'].schema} spec={spec} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* AI section */}
                <div className="mt-10 pt-6" style={{ borderTop: '1px solid var(--border)' }}>
                    <AskAiBar
                        endpointContext={`${methodUpper} ${summary.path} — ${summary.summary}`}
                        operationId={summary.operationId}
                    />
                    <AiShortcutMenu
                        method={methodUpper}
                        path={summary.path}
                        summary={summary.summary}
                    />
                </div>

                <EndpointResources
                    operationId={summary.operationId}
                    method={methodUpper}
                    path={summary.path}
                />
            </div>

            {/* Right column — code samples + response examples (GitBook docs style) */}
            <aside className="hidden xl:block sticky top-14 h-[calc(100vh-56px)] overflow-y-auto p-5"
                style={{ borderLeft: '1px solid var(--border)', backgroundColor: 'var(--surface-1)' }}>
                <div className="text-[11px] mb-2 uppercase tracking-wider font-semibold" style={{ color: 'var(--ghost)' }}>
                    Request
                </div>
                <CodeSamples method={methodUpper} path={summary.path} sampleBody={sampleBody} />

                {responses.length > 0 && (
                    <>
                        <div className="text-[11px] mb-2 mt-6 uppercase tracking-wider font-semibold" style={{ color: 'var(--ghost)' }}>
                            Response (200)
                        </div>
                        <ResponseExample spec={spec} responses={responses} />
                    </>
                )}
            </aside>
        </div>
    );
}

function ResponseExample({ spec, responses }: { spec: any; responses: [string, any][] }) {
    const success = responses.find(([c]) => c.startsWith('2'));
    if (!success) return null;
    const schema = success[1]?.content?.['application/json']?.schema;
    if (!schema) return null;
    const sample = buildSampleFromSchema(spec, resolveSchema(spec, schema));
    return <CodeBlock code={JSON.stringify(sample, null, 2)} lang="json" />;
}

function ErrorState({ message }: { message: string }) {
    return (
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
            <p className="text-sm text-muted">{message}</p>
        </div>
    );
}

// Build a sample JSON value from schema — uses examples or schema-derived placeholders
function buildSampleFromSchema(spec: any, schema: any): any {
    if (!schema) return null;
    if (schema.example !== undefined) return schema.example;
    if (schema.$ref) return buildSampleFromSchema(spec, resolveSchema(spec, schema));
    if (schema.type === 'object' && schema.properties) {
        const obj: any = {};
        for (const [key, prop] of Object.entries(schema.properties)) {
            obj[key] = buildSampleFromSchema(spec, prop);
        }
        return obj;
    }
    if (schema.type === 'array' && schema.items) {
        return [buildSampleFromSchema(spec, schema.items)];
    }
    if (schema.enum) return schema.enum[0];
    if (schema.example !== undefined) return schema.example;
    if (schema.default !== undefined) return schema.default;
    // Type-based defaults
    switch (schema.type) {
        case 'string': return schema.format === 'date-time' ? '2026-05-19T10:00:00Z' : '<string>';
        case 'number':
        case 'integer': return 0;
        case 'boolean': return false;
        case 'null': return null;
        default: return null;
    }
}
